import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { TroveComponent } from '../trove.component';
import { LogService } from 'app/services/log.service';
import { DocumentSelectionComponent } from '../document-selection/document-selection.component';
import { UntypedFormGroup } from '@angular/forms';
import { DocumentsService } from 'app/services/documents.service';
import { SupportingDocument, DocumentCategory } from '../model/supporting-document';
import { TeamType } from '../model/customer';

@Component({
  selector: 'app-documents-form',
  templateUrl: './documents-form.component.html',
  styleUrls: ['./documents-form.component.css']
})
export class DocumentsFormComponent extends TroveComponent implements AfterViewInit {

  @Input() teamType: TeamType = 'recipient';
  @Input() isPlanner: boolean = false;
  @Output() backFromDocuments: EventEmitter<SupportingDocument[]> = new EventEmitter();
  @Output() submitDocuments: EventEmitter<SupportingDocument[]> = new EventEmitter();

  @ViewChild('contractOfService', {static: false}) contractOfService: DocumentSelectionComponent;
  @ViewChild('probate', {static: false}) probate: DocumentSelectionComponent;
  @ViewChild('will', {static: false}) will: DocumentSelectionComponent;
  @ViewChild('statutoryDeclaration', {static: false}) statutoryDeclaration: DocumentSelectionComponent;
  @ViewChild('otherDocument', {static: false}) otherDocument: DocumentSelectionComponent;
  @ViewChild('powerOfAttorney', {static: false}) powerOfAttorney: DocumentSelectionComponent;
  @ViewChild('powerOfAttorneyPersonalCare', {static: false}) powerOfAttorneyPersonalCare: DocumentSelectionComponent;
  @ViewChild('advancedCarePlan', {static: false}) advancedCarePlan: DocumentSelectionComponent;
  @ViewChild('birthCertificate', {static: false}) birthCertificate: DocumentSelectionComponent;
  @ViewChild('marriageCertificate', {static: false}) marriageCertificate: DocumentSelectionComponent;
  @ViewChild('dissolutionOfMarriage', {static: false}) dissolutionOfMarriage: DocumentSelectionComponent;
  @ViewChild('contractingOutAgreement', {static: false}) contractingOutAgreement: DocumentSelectionComponent;
  @ViewChild('trustDeed', {static: false}) trustDeed: DocumentSelectionComponent;

  contractOfServiceForm: UntypedFormGroup;
  formErrors = {
    'noServiceSelected': '',
    'save': ''
  };

  validationMessages = {
  };

  private viewInitComplete = false;

  get showWill(): boolean {
    if (this.viewInitComplete === false) {
      return true;
    } else if (this.probate && this.probate.status === 'wait') {
      return true;
    } else {
      return false;
    }
  }

  get tooBig(): boolean {
    if (this.isPlanner) {
      return (this.will && this.will.status === 'too-big') ||
        (this.powerOfAttorney && this.powerOfAttorney.status === 'too-big') ||
        (this.powerOfAttorneyPersonalCare && this.powerOfAttorneyPersonalCare.status === 'too-big') ||
        (this.advancedCarePlan && this.advancedCarePlan.status === 'too-big') ||
        (this.otherDocument && this.otherDocument.status === 'too-big');
    } else {
      return (this.contractOfService && this.contractOfService.status === 'too-big') ||
        (this.probate && this.probate.status === 'too-big') ||
        (this.will && this.will.status === 'too-big') ||
        (this.statutoryDeclaration && this.statutoryDeclaration.status === 'too-big');
    }
  }

  get isLawyerMode(): boolean {
    return this.teamType === 'lawFirm';
  }

  constructor(
    private documentsService: DocumentsService,
    private logService: LogService
  ) {
    super();
  }

  public ngAfterViewInit(): void {
    this.logService.log(`DocumentsFormComponent ngAfterViewInit`);
      if (this.documentsService.documents) {
        if (this.isPlanner) {
          this.initialise(this.will, this.documentsService.documents.filter(doc => doc.category === 'will').pop());
          this.initialise(this.powerOfAttorney, this.documentsService.documents.filter(doc => doc.category === 'powerOfAttorney').pop());
          this.initialise(this.powerOfAttorneyPersonalCare, this.documentsService.documents.filter(doc => doc.category === 'powerOfAttorneyPersonalCare').pop());
          this.initialise(this.advancedCarePlan, this.documentsService.documents.filter(doc => doc.category === 'advancedCarePlan').pop());
          this.initialise(this.birthCertificate, this.documentsService.documents.filter(doc => doc.category === 'birthCertificate').pop());
          this.initialise(this.marriageCertificate, this.documentsService.documents.filter(doc => doc.category === 'marriageCertificate').pop());
          this.initialise(this.dissolutionOfMarriage, this.documentsService.documents.filter(doc => doc.category === 'dissolutionOfMarriage').pop());
          this.initialise(this.contractingOutAgreement, this.documentsService.documents.filter(doc => doc.category === 'contractingOutAgreement').pop());
          this.initialise(this.trustDeed, this.documentsService.documents.filter(doc => doc.category === 'trustDeed').pop());
          this.initialise(this.otherDocument, this.documentsService.documents.filter(doc => doc.category === 'otherDocument').pop());
        } else {
          this.initialise(this.contractOfService, this.documentsService.documents.filter(doc => doc.category === 'contractOfService').pop());
          this.initialise(this.probate, this.documentsService.documents.filter(doc => doc.category === 'probate').pop());
          this.initialise(this.will, this.documentsService.documents.filter(doc => doc.category === 'will').pop());
          this.initialise(this.statutoryDeclaration, this.documentsService.documents.filter(doc => doc.category === 'statutoryDeclaration').pop());
          this.initialise(this.otherDocument, this.documentsService.documents.filter(doc => doc.category === 'otherDocument').pop());
        }
      }
      this.viewInitComplete = true;
  }


  public onBack() {
    this.backFromDocuments.emit(this.getDocuments());
  }

  public onSubmit() {
    this.submitDocuments.emit(this.getDocuments());
  }
  
  public categoryDescription(category: DocumentCategory, suffix?: string, prefix?: string): string {
    return this.documentsService.categoryDescription(category, suffix, prefix);
  }

  /** The current selections, including any not yet handed to the parent via Next / Back. */
  public getDocuments(): SupportingDocument[] {
    const documents: SupportingDocument[] = [];
    if (this.isPlanner) {
      documents.push(this.getDocument(this.will, 'will'));
      documents.push(this.getDocument(this.powerOfAttorney, 'powerOfAttorney'));
      documents.push(this.getDocument(this.powerOfAttorneyPersonalCare, 'powerOfAttorneyPersonalCare'));
      documents.push(this.getDocument(this.advancedCarePlan, 'advancedCarePlan'));
      documents.push(this.getDocument(this.birthCertificate, 'birthCertificate'));
      documents.push(this.getDocument(this.marriageCertificate, 'marriageCertificate'));
      documents.push(this.getDocument(this.dissolutionOfMarriage, 'dissolutionOfMarriage'));
      documents.push(this.getDocument(this.contractingOutAgreement, 'contractingOutAgreement'));
      documents.push(this.getDocument(this.trustDeed, 'trustDeed'));
      documents.push(this.getDocument(this.otherDocument, 'otherDocument'));
    } else {
      documents.push(this.getDocument(this.contractOfService, 'contractOfService'));
      documents.push(this.getDocument(this.probate, 'probate'));
      if (this.will) {
        documents.push(this.getDocument(this.will, 'will'));
      }
      documents.push(this.getDocument(this.statutoryDeclaration, 'statutoryDeclaration'));
      documents.push(this.getDocument(this.otherDocument, 'otherDocument'));
    }
    return documents;
  }

  private getDocument(component: DocumentSelectionComponent, category: DocumentCategory): SupportingDocument {
    return {
      status: component.status,
      category: category,
      name: component.filename || (category.charAt(0).toUpperCase() + category.substring(1)),
      data: component.data,
      url: component.url,
      physicalLocation: component.physicalLocation,
    };
  }

  private initialise(component: DocumentSelectionComponent, document: SupportingDocument) {
    if (component && document) {
      component.filename = document.name;
      component.data = document.data;
      component.url = document.url;
      component.status = document.status;
      component.physicalLocation = document.physicalLocation;
      if (document.data) {
        component.generateThumbnail(document.data);
      }
    }
  }
}
